import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ResponsiveTable, { ResponsiveTableColumn } from '../components/common/ResponsiveTable';
import apiService from '../services/api.service';
import { ITEM_REQUEST_STATUS, type ItemRequestCapabilities, type ItemRequestRow } from '../types/itemRequests';

/** Warehouse > Item Requests: the new item requests of the selected company. */

type Scope = 'waiting' | 'mine' | 'all';

const formatDate = (value: string | null | undefined): string =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';

const ItemRequestList: React.FC = () => {
  const navigate = useNavigate();
  const companyId = useMemo(() => Number(localStorage.getItem('selectedCompanyId') || '') || 0, []);
  const [caps, setCaps] = useState<ItemRequestCapabilities | null>(null);
  const [scope, setScope] = useState<Scope | null>(null);
  const [rows, setRows] = useState<ItemRequestRow[]>([]);
  const [groups, setGroups] = useState<Map<number, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The user's rights come from PRPO alone, so the list opens at once; the item group names
  // come from SAP and fill in when they arrive (or not, when SAP does not answer).
  useEffect(() => {
    if (!companyId) { setError('Select a company first.'); return undefined; }
    let cancelled = false;
    apiService.getItemRequestCapabilities()
      .then((res: any) => {
        if (cancelled) return;
        const c: ItemRequestCapabilities = res.data;
        setCaps(c);
        setScope(c.finance || c.procurement ? 'waiting' : 'mine');
      })
      .catch(() => {
        if (cancelled) return;
        setCaps({ admin: false, finance: false, procurement: false, viewAll: false });
        setScope('mine');
      });
    apiService.getItemRequestLookups(companyId)
      .then((res: any) => { if (!cancelled) setGroups(new Map((res.data.itemGroups || []).map((g: any) => [g.code, g.name]))); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !scope) return;
    let cancelled = false;
    setLoading(true);
    apiService.listItemRequests(companyId, scope)
      .then((res: any) => { if (!cancelled) setRows(Array.isArray(res?.data) ? res.data : []); })
      .catch((e: any) => { if (!cancelled) setError(e?.response?.data?.message || 'The requests could not be loaded'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, scope]);

  const columns: ResponsiveTableColumn<ItemRequestRow>[] = [
    { id: 'no', header: 'Request', cell: (r) => <Typography variant="body2" fontWeight={600}>{r.requestno}</Typography>, width: 120 },
    {
      id: 'item',
      header: 'Item',
      cell: (r) => (
        <Box>
          <Typography variant="body2">{r.itemname}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{r.sapitemcode || r.itemcode || 'No number yet'}</Typography>
        </Box>
      ),
    },
    { id: 'group', header: 'Item Group', cell: (r) => (r.itemsgroupcode ? groups.get(r.itemsgroupcode) || r.itemsgroupcode : ''), hideOnMobile: true },
    { id: 'class', header: 'Classification', cell: (r) => r.itemclass || '', hideOnMobile: true },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => {
        const s = ITEM_REQUEST_STATUS[r.status] || { label: r.status, color: 'default' as const };
        return <Chip size="small" color={s.color} label={s.label} />;
      },
    },
    { id: 'by', header: 'Requested by', cell: (r) => r.createdbyname || '', hideOnMobile: true },
    { id: 'updated', header: 'Last change', cell: (r) => formatDate(r.updateddate), hideOnMobile: true },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'grid', gap: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: 'center' }}>
        <Box>
          <Typography variant="h5" component="h1">Item Requests</Typography>
          <Typography variant="body2" color="text.secondary">New SAP items, from request to creation in SAP.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/warehouse/item-requests/new')}>Create New Item</Button>
      </Stack>
      {scope && caps && (
        <Tabs value={scope} onChange={(_e, v) => setScope(v)} variant="scrollable" allowScrollButtonsMobile aria-label="Which requests">
          {(caps.finance || caps.procurement) && <Tab value="waiting" label="Waiting for me" />}
          <Tab value="mine" label="My requests" />
          {caps.viewAll && <Tab value="all" label="All requests" />}
        </Tabs>
      )}
      {error && <Alert severity="warning">{error}</Alert>}
      {loading || (!scope && !error) ? (
        <Stack direction="row" spacing={1.5} alignItems="center" role="status"><CircularProgress size={20} /><Typography>Loading…</Typography></Stack>
      ) : (
        <ResponsiveTable
          ariaLabel="Item requests"
          columns={columns}
          rows={rows}
          getRowKey={(r) => r.requestid}
          onRowClick={(r) => navigate(`/warehouse/item-requests/${r.requestid}`)}
          emptyMessage={scope === 'waiting' ? 'Nothing is waiting for you.' : 'No item requests yet.'}
        />
      )}
    </Box>
  );
};

export default ItemRequestList;
